import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { useMediaQuery } from "react-responsive";
import { getPrivateDegrees, getPrivateDegreeSearchOptions } from "../../services/DegreeService";
import { Header } from "../../components/shared/Header";
import { NoData } from "../../components/shared/NoData";
import { Select } from "../../components/shared/Select";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { Option } from "../../models/Option";
import { Button } from "../../components/shared/Button";
import { TableLoader } from "../../components/loaders/TableLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";
import { DegreeModel } from "../../models/DegreeModel";
import { DegreeSearchCriteria } from "../../models/DegreeSearchCriteria";

export const PrivateDegreeListPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();     
    const styleContext = useContext(StyleContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("degreeListPage"));
    const authContext = useContext(AuthContext);    
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();  
    const categoryId: number | undefined = searchParams.get("category-id") === null ? undefined : +searchParams.get("category-id")!;
    const universityId: number | undefined = searchParams.get("university-id") === null ? undefined : +searchParams.get("university-id")!;
    const degreePeriodId: number | undefined = searchParams.get("degree-period-id") === null ? undefined : +searchParams.get("degree-period-id")!;
    const keyword: string | undefined = searchParams.get("keyword") === null ? undefined : searchParams.get("keyword")!;     
    const [categories, setCategories] = useState<Option[]>([]);
    const [universities, setUniversities] = useState<Option[]>([]);
    const [degreePeriods, setDegreePeriods] = useState<Option[]>([]);
    const [searchKeyword, setSearchKeyword] = useState<string>("");
    const [degrees, setDegrees] = useState<DegreeModel[]>([]);    
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();   
    const [hoveredIndex, setHoveredIndex] = useState<number>(-1);
    const [loading, setLoading] = useState<boolean>(true);
    const [optionLoading, setOptionLoading] = useState<boolean>(true);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();

    useEffect(() => {
        if(admin === null) {
            toAdminLogin("Please login to access this page.");
        }
        else {
            getSearchOptions();
        }
    }, []);

    useEffect(() => {
        getDegreeList();                                    
    }, [categoryId, universityId, degreePeriodId, keyword]);
    
    useEffect(() => {        
        setStyles(styleContext.getComponentStyle("degreeListPage")); 
    }, [isMobile]);    

    async function getSearchOptions() {                
        await getPrivateDegreeSearchOptions()
        .then((options: any) => {
            setCategories(options.categories);
            setUniversities(options.universities);
            setDegreePeriods(options.degreePeriods);
            setOptionLoading(false);            
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    async function getDegreeList() {                
        setLoading(true);   
        let searchCriteria: DegreeSearchCriteria = {
            pageNo: 1,
            recordsPerPage: 500,
            categoryId: categoryId,
            universityId: universityId,
            degreePeriodId: degreePeriodId,
            keyword: keyword,
            languageCode: "SIN"
        }      
        await getPrivateDegrees(searchCriteria)
        .then((degreeList: DegreeModel[]) => {
            setDegrees(degreeList);  
            setLoading(false);
        })
        .catch((error) => {            
            toErrorPage(error);
        });                       
    }

    const setCategory = (selectedCategoryId: string) => {         
        if(selectedCategoryId) {        
            searchParams.set("category-id", selectedCategoryId);
        }
        else {
            searchParams.delete("category-id");
        }
        handleNavigate();
    }    

    const setUniversity = (selectedUniversityId: string) => {         
        if(selectedUniversityId) {        
            searchParams.set("university-id", selectedUniversityId);
        }
        else {
            searchParams.delete("university-id");
        }
        handleNavigate();
    } 

    const setDegreePeriod = (selectedPeriodId: string) => {         
        if(selectedPeriodId) {        
            searchParams.set("degree-period-id", selectedPeriodId);
        }
        else {
            searchParams.delete("degree-period-id");
        }
        handleNavigate();
    } 
    
    const setKeyword = (keywordInput: string) => {
        if(keywordInput === "") {
            searchParams.delete("keyword");
        }   
        else {                
            searchParams.set("keyword", keywordInput);
        }
        handleNavigate();
    }
    
    const handleKeywordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        event.stopPropagation();  
        const value = event.target.value;
        setSearchKeyword(value);

        // Clear previous timeout
        if (timeoutId) {
            clearTimeout(timeoutId);
        }

        // Set new timeout
        const newTimeoutId = setTimeout(() => {
            setKeyword(value.trim());
        }, 1000);
        setTimeoutId(newTimeoutId);
    };

    const handleNavigate = () => {                                    
        const newUrl = `/private-degrees?${searchParams}`;
        navigate(newUrl);                
    }

    const navigateToStateDegrees = () => {
        navigate("/state-degrees");
    }

    const navigateToEdit = (degreeId: number) => {
        navigate("/private-degree/edit/" + degreeId);
    }

    return (   
        <>
            <Header /> 
            <div style={styles}>                         
                <div style={styles.accordionButtonsContainer}>
                    <div 
                        style={{ ...styles.accordionButton, ...styles.accordionButton.left }}
                        onClick={navigateToStateDegrees}
                    >
                        {"State Degrees"}
                    </div>
                    <div style={{ ...styles.accordionButton, ...styles.accordionButton.right, ...styles.accordionButton.selected }}>
                        {"Private Degrees"}
                    </div>
                </div> 
                {optionLoading ? <SelectLoader count={4} />  :      
                <div style={styles.searchWrapper}>
                    {categories.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Categories"} 
                        options={categories} 
                        initialValue={categoryId?.toString()}
                        getSelectedValue={setCategory} 
                    />}
                    {universities.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Universities"} 
                        options={universities} 
                        initialValue={universityId?.toString()}
                        getSelectedValue={setUniversity} 
                    />}
                    {degreePeriods.length > 0 && <Select 
                        styles={styles.searchSelect}
                        label={"Degree Periods"} 
                        options={degreePeriods} 
                        initialValue={degreePeriodId?.toString()}
                        searchDisabled={true}
                        getSelectedValue={setDegreePeriod} 
                    />}                    
                    <input
                        type={"text"}
                        placeholder="Search by keywords"
                        value={searchKeyword}
                        onChange={(e) => handleKeywordChange(e)} 
                        style={styles.searchInput}                                                                                    
                    />
                </div>}
                {loading ? <TableLoader columnCount={5} rowCount={10} /> : 
                <div>                
                    {(degrees.length === 0) ? <NoData/> :
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.table.header}> 
                                <th style={styles.table.idColumn}>{"Id"}</th>                           
                                <th style={styles.table.universityColumn}>{"University"}</th>
                                <th style={styles.table.degreeColumn}>{"Degree"}</th>
                                <th style={styles.table.periodColumn}>{"Period"}</th>                               
                            </tr>
                        </thead>
                        <tbody>
                            {degrees.map((degree, index) => (
                                <tr 
                                    key={index} 
                                    style={{...styles.table.tr, ...(index === hoveredIndex && styles.table.hoveredTr)}}
                                    onMouseEnter={() => setHoveredIndex(index)}
                                    onMouseLeave={() => setHoveredIndex(-1)}
                                >
                                    <td>{degree.degreeId}</td>
                                    <td>
                                        <div style={styles.table.rowWrapper}>
                                            <div>
                                                <img src={degree.universityLogoUrl} style={styles.table.logo}></img>
                                            </div>
                                            <div>
                                                {degree.universityName}<br/>
                                                {degree.universityName_ENG}
                                            </div>
                                        </div>
                                    </td>                                   
                                    <td>
                                        {degree.degreeName}<br/>
                                        {degree.degreeName_ENG}                                        
                                    </td>
                                    <td style={styles.table.periodCell}>
                                        <div>
                                            {degree.degreePeriodName}<br/>
                                            {degree.degreePeriodName_ENG}
                                        </div>
                                        {index === hoveredIndex && <Button style={styles.table.editButton} onClick={() => navigateToEdit(degree.degreeId)}>Edit</Button>}
                                    </td>                                                                       
                                </tr>
                            ))}
                        </tbody>
                    </table>}                    
                </div>}
            </div>
        </>         
    )
}